import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../core/services/base.httpService';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import { CommonService } from 'src/app/shared/services/common.service';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { isAddressVerificationRejection } from 'src/app/shared/utility/address-verification.util';
@Injectable({
  providedIn: 'root',
})
export class AirWayBillService {
  private readonly errorHandler = handleErrorMessage();
  /** Address verification rejections are handled by the caller (re-verify dialog), not a toast. */
  private readonly addressSaveErrorConfig = {
    skipErrorToast: isAddressVerificationRejection,
  };

  constructor(
    public baseService: BaseService,
    private readonly commonService: CommonService
  ) {}

  public getLookUp(data: string): Observable<any> {
    const url = URLConstants.lookup + data;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public getMasterLookup(lookupKey: string): Observable<any> {
    const url = URLConstants.masterLookup + lookupKey;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public getStateData(data: any): Observable<any> {
    const url = URLConstants.getStateDetails + data;
    return this.baseService
      .initiateApiRequest('GET', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public getCityData(data: any): Observable<any> {
    const url = URLConstants.getCitiesList + data;
    return this.baseService
      .initiateApiRequest('GET', url, data, 'json')
      .pipe(this.errorHandler());
  }

  /**
   * Accounts the signed-in IBC user is linked to (empty for external
   * customers). Drives the Shipper Account dropdown (SPEC-AIU-001 AC7/AC8).
   */
  public getLinkedAccounts(): Observable<any> {
    return this.baseService
      .initiateApiRequest('GET', URLConstants.linkedAccounts, '', 'json')
      .pipe(this.errorHandler());
  }

  public fetchAddressBasedOnAccount(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.addressBasedOnAccount + payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public saveAddressBook(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.addressSave, payload, '', 'json')
      .pipe(this.errorHandler(this.addressSaveErrorConfig));
  }

  public updateAddressBook(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'PUT',
        URLConstants.addressUpdate,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler(this.addressSaveErrorConfig));
  }

  public deleteAddressBook(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.addressDelete,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getShipperAddressBookList(): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.shipperAddressBookList,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public fetchShipperAddressBookById(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.fetchShipperAddressById,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public fetchAddressDetails(): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.fetchAddresBookDetails,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getConsigneeAddressBookList(): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.consigneeAddressBookList,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public markAsFavourite(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.addressFavoutite,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public generateAirwayBill(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.generateAirwayBill,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler(this.addressSaveErrorConfig));
  }

  public getHsCodeData(data: string): Observable<any> {
    const url = URLConstants.getHsCodeByDescription + data;
    return this.baseService
      .initiateApiRequest('GET', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public historyFetch(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.airwayHistory, payload, '', 'json')
      .pipe(this.errorHandler());
  }

  public formatPieceDimensions(pieceData: {
    dimL?: string | number | null;
    dimW?: string | number | null;
    dimH?: string | number | null;
  }): string {
    const format = (value: string | number | null | undefined) =>
      value === null || value === undefined || String(value).trim() === ''
        ? ''
        : String(value).trim();
    const parts = [
      format(pieceData.dimL),
      format(pieceData.dimW),
      format(pieceData.dimH),
    ].filter(part => part !== '');
    return parts.length ? parts.join(' x ') : '';
  }

  public hasCompleteDimensions(pieceData: {
    dimL?: string | number | null;
    dimW?: string | number | null;
    dimH?: string | number | null;
  }): boolean {
    const hasValue = (value: string | number | null | undefined) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '';
    return (
      hasValue(pieceData.dimL) &&
      hasValue(pieceData.dimW) &&
      hasValue(pieceData.dimH)
    );
  }

  public computeVolumetricNumeric(pieceData: any): number {
    if (!this.hasCompleteDimensions(pieceData)) {
      return 0;
    }
    const length = Number(pieceData.dimL);
    const width = Number(pieceData.dimW);
    const height = Number(pieceData.dimH);
    if (length <= 0 || width <= 0 || height <= 0) {
      return 0;
    }
    let factor = 139;
    if (
      this.commonService.setMeasurementOptionsValues().sizeValue ===
      'Centimeters'
    ) {
      factor = 5000;
    }
    return Math.ceil(Number(((length * width * height) / factor).toFixed(2)));
  }

  public pieceGridDataModification(incomingPieceData: any) {
    const modifiedPieceData = {
      ...incomingPieceData,
      dimensions: this.formatPieceDimensions(incomingPieceData),
      calculated:
        (Number(incomingPieceData.weight) * Number(incomingPieceData.multiplier)).toFixed(3),
      volumetric: this.volumetricCalculation(incomingPieceData),
    };
    return modifiedPieceData;
  }

  public volumetricCalculation(pieceData: any) {
    let units = 'lbs';
    if (
      this.commonService.setMeasurementOptionsValues().weightValue ===
      'Kilograms'
    ) {
      units = 'kgs';
    }
    const volume = this.computeVolumetricNumeric(pieceData);
    return volume + ' ' + units;
  }
}
